import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { toast } from "sonner";
import {
  Download,
  LogOut,
  Check,
  Users,
  UserCheck,
  Bed,
  Car,
  Trash2,
  KeyRound,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-[#262626] bg-[#0A0A0A] p-6" data-testid={testid}>
    <Icon className="h-5 w-5 text-[#D4CBB3]" strokeWidth={1.5} />
    <p className="mt-4 font-display text-4xl font-light text-white">{value}</p>
    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-400">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sitePw, setSitePw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rsvps");
      setRsvps(data);
      const pw = await api.get("/settings/site-password");
      setSitePw(pw.data.password);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/admin/login");
    }
  };

  const saveSitePw = async () => {
    setSavingPw(true);
    try {
      const { data } = await api.post("/settings/site-password", { password: sitePw });
      toast.success(`Gästlösenordet är nu "${data.password}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  const toggleConfirm = async (id) => {
    try {
      const { data } = await api.patch(`/rsvps/${id}/confirm`);
      setRsvps((prev) => prev.map((r) => (r.id === id ? data : r)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Vill du ta bort detta svar?")) return;
    try {
      await api.delete(`/rsvps/${id}`);
      setRsvps((prev) => prev.filter((r) => r.id !== id));
      toast.success("Svar borttaget");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const headers = [
      "Anmälnings-ID",
      "Förnamn",
      "Efternamn",
      "Kommer",
      "Allergier/Kost",
      "E-post",
      "Telefon",
      "Meddelande",
      "Sent svar",
      "Bekräftad",
    ];
    const rows = rsvps.flatMap((r) =>
      (r.guests || []).map((g) => [
        r.id,
        g.first_name,
        g.last_name,
        g.attending ? "Ja" : "Nej",
        g.allergies || "",
        r.email,
        r.phone || "",
        r.message || "",
        r.late ? "Ja" : "Nej",
        r.confirmed ? "Ja" : "Nej",
      ])
    );
    const csv = [headers, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "osa-svar.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const allGuests = rsvps.flatMap((r) => r.guests || []);
  const attending = allGuests.filter((g) => g.attending).length;
  const confirmed = rsvps.filter((r) => r.confirmed).length;
  const accommodation = allGuests.length;
  const transport = rsvps.filter((r) => r.late).length;

  return (
    <div className="min-h-screen bg-[#050505] px-4 py-8 md:px-10 md:py-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4CBB3]">Administration</p>
            <h1 className="mt-2 font-display text-3xl font-light tracking-tight text-white md:text-4xl">
              OSA-svar
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportCsv}
              data-testid="export-csv-button"
              className="inline-flex items-center gap-2 bg-[#D4CBB3] px-5 py-3 text-sm font-medium uppercase tracking-[0.1em] text-[#050505] transition-colors hover:bg-[#C2B79A]"
            >
              <Download className="h-4 w-4" /> Exportera CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-[#262626] px-5 py-3 text-sm text-neutral-300 transition-colors hover:border-[#3a3a3a] hover:text-white"
            >
              <LogOut className="h-4 w-4" /> Logga ut
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat icon={Users} label="Anmälningar" value={rsvps.length} testid="stat-total" />
          <Stat icon={UserCheck} label="Gäster som kommer" value={attending} testid="stat-attending" />
          <Stat icon={Bed} label="Antal gäster totalt" value={accommodation} testid="stat-accommodation" />
          <Stat icon={Car} label="Sena svar" value={transport} testid="stat-transport" />
        </div>

        <p className="mt-6 text-sm text-neutral-500" data-testid="confirmed-count">
          {confirmed} av {rsvps.length} gäster bekräftade
        </p>

        <div className="mt-6 max-w-xl border border-[#262626] bg-[#0A0A0A] p-6" data-testid="site-password-panel">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#D4CBB3]" strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4CBB3]">Gästernas lösenord</p>
          </div>
          <p className="mt-2 text-sm text-neutral-500">
            Lösenordet gäster anger för att öppna inbjudan. Ändringen gäller direkt – även stora/små bokstäver fungerar.
          </p>
          <div className="mt-4 flex gap-3">
            <input
              value={sitePw}
              onChange={(e) => setSitePw(e.target.value)}
              data-testid="site-password-setting-input"
              className="h-11 flex-1 border border-[#262626] bg-[#050505] px-4 text-white outline-none focus:border-[#D4CBB3]"
            />
            <button
              onClick={saveSitePw}
              disabled={savingPw}
              data-testid="site-password-save-button"
              className="bg-[#D4CBB3] px-6 text-sm font-medium uppercase tracking-[0.1em] text-[#050505] transition-colors hover:bg-[#C2B79A] disabled:opacity-50"
            >
              {savingPw ? "Sparar…" : "Spara"}
            </button>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto border border-[#262626]">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="rsvp-table">
            <thead>
              <tr className="border-b border-[#262626] text-xs uppercase tracking-[0.1em] text-neutral-500">
                <th className="px-4 py-4 font-normal">Namn</th>
                <th className="px-4 py-4 font-normal">Kontakt</th>
                <th className="px-4 py-4 font-normal">Kommer</th>
                <th className="px-4 py-4 font-normal">Allergier</th>
                <th className="px-4 py-4 font-normal">Flagga</th>
                <th className="px-4 py-4 font-normal">Status</th>
                <th className="px-4 py-4 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-neutral-500">
                    Laddar…
                  </td>
                </tr>
              ) : rsvps.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-neutral-500" data-testid="empty-state">
                    Inga svar ännu.
                  </td>
                </tr>
              ) : (
                rsvps.map((r) => (
                  <tr key={r.id} className="border-b border-[#1a1a1a] hover:bg-[#0A0A0A]" data-testid={`rsvp-row-${r.id}`}>
                    <td className="px-4 py-4 text-white">
                      {(r.guests || []).map((g, gi) => (
                        <div key={gi}>
                          {g.first_name} {g.last_name}
                          {gi > 0 && <span className="ml-2 text-xs text-neutral-500">(medföljande)</span>}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-4 text-neutral-400">
                      <div>{r.email}</div>
                      <div>{r.phone}</div>
                    </td>
                    <td className="px-4 py-4">
                      {(r.guests || []).map((g, gi) => (
                        <div key={gi} className={g.attending ? "text-[#D4CBB3]" : "text-red-400"}>
                          {g.attending ? "Ja" : "Nej"}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-4 text-neutral-400">
                      {(r.guests || []).map((g, gi) => (
                        <div key={gi}>{g.allergies || "—"}</div>
                      ))}
                    </td>
                    <td className="px-4 py-4 text-neutral-400">
                      {r.late ? <span className="text-amber-400">Sent svar</span> : "—"}
                    </td>
                    <td className="px-4 py-4">
                      {r.confirmed ? (
                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#D4CBB3]">
                          <Check className="h-3 w-3" /> Bekräftad
                        </span>
                      ) : (
                        <span className="text-xs uppercase tracking-wider text-neutral-600">Väntar</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleConfirm(r.id)}
                          data-testid={`confirm-button-${r.id}`}
                          className={`border px-3 py-2 text-xs transition-colors ${
                            r.confirmed
                              ? "border-[#262626] text-neutral-400 hover:text-white"
                              : "border-[#D4CBB3] text-[#D4CBB3] hover:bg-[#D4CBB3] hover:text-[#050505]"
                          }`}
                        >
                          {r.confirmed ? "Ångra" : "Bekräfta"}
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          data-testid={`delete-button-${r.id}`}
                          className="border border-[#262626] p-2 text-neutral-500 transition-colors hover:border-red-900 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
